import React, { useState } from 'react';
import { Search, CheckCircle, XCircle, AlertCircle, Loader2, Mail } from 'lucide-react';

interface PurchaseStatusCheckerProps {
  className?: string;
}

export const PurchaseStatusChecker: React.FC<PurchaseStatusCheckerProps> = ({ className = '' }) => {
  const [emailToCheck, setEmailToCheck] = useState('');
  const [isChecking, setIsChecking] = useState(false);
  const [checkResult, setCheckResult] = useState<any>(null);

  const handleCheckEmail = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!emailToCheck.trim()) {
      return;
    }

    setIsChecking(true);
    setCheckResult(null);

    try {
      // Simulate checking - this feature is not available yet
      setTimeout(() => {
        setCheckResult({
          email: emailToCheck,
          error: 'Purchase checking is not available yet. This feature will be enabled at launch.'
        });
        setIsChecking(false);
      }, 1000);
      
    } catch (error) {
      console.error('Error checking purchase status:', error);
      setCheckResult({
        email: emailToCheck,
        error: 'Purchase checking is not available yet'
      });
      setIsChecking(false);
    }
  };

  return (
    <div className={`bg-white rounded-xl shadow-lg border border-gray-200 p-6 ${className}`}>
      <div className="flex items-center space-x-3 mb-6">
        <div className="w-10 h-10 bg-blue-100 rounded-xl flex items-center justify-center">
          <Search className="w-5 h-5 text-blue-600" />
        </div>
        <h3 className="text-xl font-bold text-gray-900">Check Purchase Status</h3>
      </div>

      <form onSubmit={handleCheckEmail} className="space-y-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Email Address
          </label>
          <div className="relative">
            <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
            <input
              type="email"
              value={emailToCheck}
              onChange={(e) => setEmailToCheck(e.target.value)}
              className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder="Enter email to check purchase status"
              required
              disabled={isChecking}
            />
          </div>
        </div>

        <button
          type="submit"
          disabled={isChecking || !emailToCheck.trim()}
          className="w-full flex items-center justify-center space-x-2 bg-blue-600 text-white px-4 py-3 rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {isChecking ? (
            <>
              <Loader2 className="w-4 h-4 animate-spin" />
              <span>Checking...</span>
            </>
          ) : (
            <>
              <Search className="w-4 h-4" />
              <span>Check Purchase Status</span>
            </>
          )}
        </button>
      </form>

      {/* Results */}
      {checkResult && (
        <div className="mt-6 p-4 border border-gray-200 rounded-lg">
          <div className="flex items-center space-x-3 mb-3">
            {checkResult.error ? (
              <XCircle className="w-5 h-5 text-red-500" />
            ) : checkResult.hasPurchased ? (
              <CheckCircle className="w-5 h-5 text-green-500" />
            ) : (
              <AlertCircle className="w-5 h-5 text-orange-500" />
            )}
            <span className="font-medium text-gray-900">
              Results for: {checkResult.email}
            </span>
          </div>

          {checkResult.error ? (
            <p className="text-red-600 text-sm">{checkResult.error}</p>
          ) : checkResult.hasPurchased ? (
            <div className="space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-gray-600">Status:</span>
                <span className="font-medium text-green-700">Purchase Confirmed</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Plan:</span>
                <span className="font-medium text-gray-900">{checkResult.planName}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Purchase Date:</span>
                <span className="font-medium text-gray-900">
                  {new Date(checkResult.purchaseDate).toLocaleDateString()}
                </span>
              </div>
            </div>
          ) : (
            <p className="text-orange-600 text-sm">No purchase found for this email address.</p>
          )}
        </div>
      )}
    </div>
  );
};

export default PurchaseStatusChecker;